import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import cars from "../data/cars";
import AdminFleetCalendar from "../components/AdminFleetCalendar";

import "./AdminBookings.css";

const ADMIN_USER_ID =
  "6dd6fb2d-8b2f-41f3-9cc7-490c968fc903";

function AdminBookings() {
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [notes, setNotes] = useState({});

  const [activeTab, setActiveTab] =
    useState("pending");

  const [
    selectedCalendarCarId,
    setSelectedCalendarCarId,
  ] = useState(null);

  const [
    checkingSession,
    setCheckingSession,
  ] = useState(true);

  const [loading, setLoading] =
    useState(true);

  const [
    actionLoadingId,
    setActionLoadingId,
  ] = useState(null);

  const [message, setMessage] =
    useState("");

  const [
    showAddBooking,
    setShowAddBooking,
  ] = useState(false);

  const [
    creatingBooking,
    setCreatingBooking,
  ] = useState(false);

  const [
    manualBooking,
    setManualBooking,
  ] = useState({
    carId: "",
    customerName: "",
    customerPhone: "",
    pickupDate: "",
    pickupTime: "10:00",
    returnDate: "",
    returnTime: "10:00",
    status: "confirmed",
    adminNotes: "",
  });

  const timeOptions = useMemo(() => {
    const times = [];

    for (
      let hour = 0;
      hour < 24;
      hour += 1
    ) {
      for (
        let minute = 0;
        minute < 60;
        minute += 15
      ) {
        const formattedHour =
          String(hour).padStart(
            2,
            "0"
          );

        const formattedMinute =
          String(minute).padStart(
            2,
            "0"
          );

        times.push(
          `${formattedHour}:${formattedMinute}`
        );
      }
    }

    return times;
  }, []);

  const getCarById = (carId) => {
    return cars.find(
      (car) =>
        Number(car.id) ===
        Number(carId)
    );
  };

  const formatDate = (
    dateString
  ) => {
    if (!dateString) return "â€”";

    const [year, month, day] =
      dateString.split("-");

    return `${day}/${month}/${year}`;
  };

  const formatTime = (
    timeString
  ) => {
    if (!timeString) return "â€”";

    return timeString.slice(0, 5);
  };

  const createDateTime = (
    date,
    time
  ) => {
    if (!date || !time) return null;

    return new Date(
      `${date}T${time.slice(
        0,
        5
      )}:00`
    );
  };

  const isBookingPrevious = (
    booking
  ) => {
    const returnDateTime =
      createDateTime(
        booking.return_date,
        booking.return_time
      );

    if (!returnDateTime) {
      return false;
    }

    return (
      returnDateTime < new Date()
    );
  };

  const calculateRentalDays = (
    pickupDate,
    pickupTime,
    returnDate,
    returnTime
  ) => {
    if (
      !pickupDate ||
      !pickupTime ||
      !returnDate ||
      !returnTime
    ) {
      return 0;
    }

    const pickup = createDateTime(
      pickupDate,
      pickupTime
    );

    const dropoff = createDateTime(
      returnDate,
      returnTime
    );

    if (
      !pickup ||
      !dropoff ||
      dropoff <= pickup
    ) {
      return 0;
    }

    const pickupDay = new Date(
      pickupDate + "T00:00:00"
    );

    const returnDay = new Date(
      returnDate + "T00:00:00"
    );

    const differenceInDays =
      Math.round(
        (returnDay -
          pickupDay) /
          (1000 *
            60 *
            60 *
            24)
      );

    let rentalDays =
      differenceInDays + 1;

    const [
      pickupHour,
      pickupMinute,
    ] = pickupTime
      .slice(0, 5)
      .split(":")
      .map(Number);

    const [
      returnHour,
      returnMinute,
    ] = returnTime
      .slice(0, 5)
      .split(":")
      .map(Number);

    const pickupMinutes =
      pickupHour * 60 +
      pickupMinute;

    const returnMinutes =
      returnHour * 60 +
      returnMinute;

    if (
      pickupMinutes >=
      22 * 60
    ) {
      rentalDays -= 1;
    }

    if (
      returnMinutes <
      9 * 60
    ) {
      rentalDays -= 1;
    }

    return Math.max(
      1,
      rentalDays
    );
  };

  const loadBookings =
    useCallback(async () => {
      setLoading(true);
      setMessage("");

      try {
        const {
          data,
          error,
        } = await supabase
          .from("bookings")
          .select(`
            id,
            car_id,
            customer_name,
            customer_phone,
            pickup_date,
            pickup_time,
            return_date,
            return_time,
            status,
            admin_notes,
            created_at
          `)
          .order("created_at", {
            ascending: false,
          });

        if (error) {
          throw error;
        }

        const safeBookings =
          data || [];

        setBookings(
          safeBookings
        );

        const nextNotes = {};

        safeBookings.forEach(
          (booking) => {
            nextNotes[
              booking.id
            ] =
              booking.admin_notes ||
              "";
          }
        );

        setNotes(nextNotes);
      } catch (error) {
        console.error(
          "Could not load bookings:",
          error
        );

        setMessage(
          error?.message ||
            "Could not load bookings."
        );
      } finally {
        setLoading(false);
      }
    }, []);

  useEffect(() => {
    const checkAdmin =
      async () => {
        try {
          const {
            data: {
              session,
            },
          } =
            await supabase.auth.getSession();

          if (
            !session?.user ||
            session.user.id !==
              ADMIN_USER_ID
          ) {
            await supabase.auth.signOut();

            navigate(
              "/admin",
              {
                replace: true,
              }
            );

            return;
          }

          setCheckingSession(
            false
          );

          await loadBookings();
        } catch (error) {
          console.error(
            "Admin session error:",
            error
          );

          await supabase.auth.signOut();

          navigate(
            "/admin",
            {
              replace: true,
            }
          );
        }
      };

    checkAdmin();
  }, [
    loadBookings,
    navigate,
  ]);

  const checkConflict =
    async ({
      carId,
      pickupDate,
      pickupTime,
      returnDate,
      returnTime,
      ignoreBookingId = null,
    }) => {
      let query = supabase
        .from("bookings")
        .select(`
          id,
          car_id,
          pickup_date,
          pickup_time,
          return_date,
          return_time,
          status
        `)
        .eq(
          "car_id",
          Number(carId)
        )
        .eq(
          "status",
          "confirmed"
        )
        .lte(
          "pickup_date",
          returnDate
        )
        .gte(
          "return_date",
          pickupDate
        );

      if (
        ignoreBookingId
      ) {
        query = query.neq(
          "id",
          ignoreBookingId
        );
      }

      const {
        data,
        error,
      } = await query;

      if (error) {
        throw error;
      }

      const requestedPickup =
        createDateTime(
          pickupDate,
          pickupTime
        );

      const requestedReturn =
        createDateTime(
          returnDate,
          returnTime
        );

      const conflictingBooking =
        (data || []).find(
          (
            existingBooking
          ) => {
            const existingPickup =
              createDateTime(
                existingBooking.pickup_date,
                existingBooking.pickup_time
              );

            const existingReturn =
              createDateTime(
                existingBooking.return_date,
                existingBooking.return_time
              );

            return (
              existingPickup <
                requestedReturn &&
              existingReturn >
                requestedPickup
            );
          }
        );

      return (
        conflictingBooking ||
        null
      );
    };

  const handleConfirm =
    async (booking) => {
      const car =
        getCarById(
          booking.car_id
        );

      const confirmed =
        window.confirm(
          `Confirm booking #${booking.id} for ${
            car?.name ||
            "this car"
          }?`
        );

      if (!confirmed) return;

      setActionLoadingId(
        booking.id
      );

      setMessage("");

      try {
        const conflict =
          await checkConflict(
            {
              carId:
                booking.car_id,
              pickupDate:
                booking.pickup_date,
              pickupTime:
                booking.pickup_time,
              returnDate:
                booking.return_date,
              returnTime:
                booking.return_time,
              ignoreBookingId:
                booking.id,
            }
          );

        if (conflict) {
          setMessage(
            `Cannot confirm booking #${booking.id}. This car already has confirmed booking #${conflict.id} during that period.`
          );

          return;
        }

        const { error } =
          await supabase
            .from("bookings")
            .update({
              status:
                "confirmed",
            })
            .eq(
              "id",
              booking.id
            );

        if (error) {
          throw error;
        }

        setMessage(
          `Booking #${booking.id} confirmed successfully.`
        );

        await loadBookings();
      } catch (error) {
        console.error(
          "Confirm booking error:",
          error
        );

        setMessage(
          error?.message ||
            "Could not confirm booking."
        );
      } finally {
        setActionLoadingId(
          null
        );
      }
    };

  const handleReject =
    async (booking) => {
      const confirmed =
        window.confirm(
          `Reject booking #${booking.id}?`
        );

      if (!confirmed) return;

      setActionLoadingId(
        booking.id
      );

      setMessage("");

      try {
        const { error } =
          await supabase
            .from("bookings")
            .update({
              status:
                "rejected",
            })
            .eq(
              "id",
              booking.id
            );

        if (error) {
          throw error;
        }

        setMessage(
          `Booking #${booking.id} rejected.`
        );

        await loadBookings();
      } catch (error) {
        console.error(
          "Reject booking error:",
          error
        );

        setMessage(
          error?.message ||
            "Could not reject booking."
        );
      } finally {
        setActionLoadingId(
          null
        );
      }
    };

  const handleCancel =
    async (booking) => {
      const confirmed =
        window.confirm(
          `Cancel confirmed booking #${booking.id}?`
        );

      if (!confirmed) return;

      setActionLoadingId(
        booking.id
      );

      setMessage("");

      try {
        const { error } =
          await supabase
            .from("bookings")
            .update({
              status:
                "cancelled",
            })
            .eq(
              "id",
              booking.id
            );

        if (error) {
          throw error;
        }

        setMessage(
          `Booking #${booking.id} cancelled.`
        );

        await loadBookings();
      } catch (error) {
        console.error(
          "Cancel booking error:",
          error
        );

        setMessage(
          error?.message ||
            "Could not cancel booking."
        );
      } finally {
        setActionLoadingId(
          null
        );
      }
    };

  const handleDelete =
    async (booking) => {
      const confirmed =
        window.confirm(
          `Permanently delete booking #${booking.id}?\n\nThis cannot be undone.`
        );

      if (!confirmed) return;

      setActionLoadingId(
        booking.id
      );

      setMessage("");

      try {
        const { error } =
          await supabase
            .from("bookings")
            .delete()
            .eq(
              "id",
              booking.id
            );

        if (error) {
          throw error;
        }

        setMessage(
          `Booking #${booking.id} permanently deleted.`
        );

        await loadBookings();
      } catch (error) {
        console.error(
          "Delete booking error:",
          error
        );

        setMessage(
          error?.message ||
            "Could not delete booking."
        );
      } finally {
        setActionLoadingId(
          null
        );
      }
    };

  const handleSaveNotes =
    async (booking) => {
      setActionLoadingId(
        booking.id
      );

      setMessage("");

      try {
        const { error } =
          await supabase
            .from("bookings")
            .update({
              admin_notes:
                notes[
                  booking.id
                ]?.trim() ||
                null,
            })
            .eq(
              "id",
              booking.id
            );

        if (error) {
          throw error;
        }

        setMessage(
          `Notes saved for booking #${booking.id}.`
        );

        await loadBookings();
      } catch (error) {
        console.error(
          "Save notes error:",
          error
        );

        setMessage(
          error?.message ||
            "Could not save notes."
        );
      } finally {
        setActionLoadingId(
          null
        );
      }
    };

  const handleLogout =
    async () => {
      await supabase.auth.signOut();

      navigate(
        "/admin",
        {
          replace: true,
        }
      );
    };

  const handleManualChange =
    (event) => {
      const {
        name,
        value,
      } = event.target;

      setManualBooking(
        (current) => ({
          ...current,
          [name]: value,
        })
      );
    };

  const resetManualBooking =
    () => {
      setManualBooking({
        carId: "",
        customerName: "",
        customerPhone: "",
        pickupDate: "",
        pickupTime: "10:00",
        returnDate: "",
        returnTime: "10:00",
        status: "confirmed",
        adminNotes: "",
      });
    };

  const handleCreateBooking =
    async (event) => {
      event.preventDefault();

      setMessage("");

      if (
        !manualBooking.carId ||
        !manualBooking.customerName.trim() ||
        !manualBooking.customerPhone.trim() ||
        !manualBooking.pickupDate ||
        !manualBooking.pickupTime ||
        !manualBooking.returnDate ||
        !manualBooking.returnTime
      ) {
        setMessage(
          "Please complete all required booking fields."
        );

        return;
      }

      const pickup =
        createDateTime(
          manualBooking.pickupDate,
          manualBooking.pickupTime
        );

      const dropoff =
        createDateTime(
          manualBooking.returnDate,
          manualBooking.returnTime
        );

      if (
        !pickup ||
        !dropoff ||
        dropoff <= pickup
      ) {
        setMessage(
          "Return date and time must be after pickup."
        );

        return;
      }

      setCreatingBooking(
        true
      );

      try {
        if (
          manualBooking.status ===
          "confirmed"
        ) {
          const conflict =
            await checkConflict(
              {
                carId:
                  manualBooking.carId,
                pickupDate:
                  manualBooking.pickupDate,
                pickupTime:
                  manualBooking.pickupTime,
                returnDate:
                  manualBooking.returnDate,
                returnTime:
                  manualBooking.returnTime,
              }
            );

          if (conflict) {
            setMessage(
              `This car already has confirmed booking #${conflict.id} during the selected period.`
            );

            return;
          }
        }

        const { error } =
          await supabase
            .from("bookings")
            .insert({
              car_id: Number(
                manualBooking.carId
              ),

              customer_name:
                manualBooking.customerName.trim(),

              customer_phone:
                manualBooking.customerPhone.trim(),

              pickup_date:
                manualBooking.pickupDate,

              pickup_time:
                manualBooking.pickupTime,

              return_date:
                manualBooking.returnDate,

              return_time:
                manualBooking.returnTime,

              status:
                manualBooking.status,

              admin_notes:
                manualBooking.adminNotes.trim() ||
                null,
            });

        if (error) {
          throw error;
        }

        setMessage(
          "Manual booking created successfully."
        );

        resetManualBooking();

        setShowAddBooking(
          false
        );

        await loadBookings();
      } catch (error) {
        console.error(
          "Create booking error:",
          error
        );

        setMessage(
          error?.message ||
            "Could not create booking."
        );
      } finally {
        setCreatingBooking(
          false
        );
      }
    };

  const pendingCount =
    bookings.filter(
      (booking) =>
        booking.status ===
          "pending" &&
        !isBookingPrevious(
          booking
        )
    ).length;

  const confirmedCount =
    bookings.filter(
      (booking) =>
        booking.status ===
          "confirmed" &&
        !isBookingPrevious(
          booking
        )
    ).length;

  const previousCount =
    bookings.filter(
      (booking) =>
        isBookingPrevious(
          booking
        )
    ).length;

  const filteredBookings = useMemo(() => {
    const filtered = bookings.filter(
      (booking) => {
        if (
          selectedCalendarCarId &&
          Number(booking.car_id) !==
            Number(selectedCalendarCarId)
        ) {
          return false;
        }

        if (activeTab === "all") {
          return true;
        }

        if (activeTab === "previous") {
          return isBookingPrevious(
            booking
          );
        }

        if (activeTab === "pending") {
          return (
            booking.status ===
              "pending" &&
            !isBookingPrevious(
              booking
            )
          );
        }

        if (
          activeTab === "confirmed"
        ) {
          return (
            booking.status ===
              "confirmed" &&
            !isBookingPrevious(
              booking
            )
          );
        }

        return true;
      }
    );

    return filtered.sort(
      (bookingA, bookingB) => {
        const pickupA =
          createDateTime(
            bookingA.pickup_date,
            bookingA.pickup_time
          );

        const pickupB =
          createDateTime(
            bookingB.pickup_date,
            bookingB.pickup_time
          );

        if (!pickupA && !pickupB) {
          return 0;
        }

        if (!pickupA) {
          return 1;
        }

        if (!pickupB) {
          return -1;
        }

        if (
          activeTab === "previous"
        ) {
          return (
            pickupB.getTime() -
            pickupA.getTime()
          );
        }

        return (
          pickupA.getTime() -
          pickupB.getTime()
        );
      }
    );
  }, [
    bookings,
    activeTab,
    selectedCalendarCarId,
  ]);

  if (checkingSession) {
    return (
      <div className="admin-loading-page">
        Checking admin session...
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="admin-header-inner">
          <div className="admin-brand">
            <div className="admin-brand-logo">
              AR
            </div>

            <div>
              <div className="admin-brand-title">
                AutoRent Albania
              </div>

              <div className="admin-brand-subtitle">
                Administration
              </div>
            </div>
          </div>

          <div className="admin-header-actions">
            <button
              type="button"
              className="admin-site-button"
              onClick={() =>
                navigate("/")
              }
            >
              View Website
            </button>

            <button
              type="button"
              className="admin-logout-button"
              onClick={
                handleLogout
              }
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="admin-content">
        <section className="admin-page-heading">
          <div>
            <span className="admin-eyebrow">
              BOOKING MANAGEMENT
            </span>

            <h1>
              Reservations
            </h1>

            <p>
              Manage customer
              requests, confirmed
              reservations and
              vehicle availability.
            </p>
          </div>

          <button
            type="button"
            className="admin-primary-button"
            onClick={() =>
              setShowAddBooking(
                (current) =>
                  !current
              )
            }
          >
            {showAddBooking
              ? "Close Form"
              : "+ Add Booking"}
          </button>
        </section>

        {message && (
          <div className="admin-message">
            {message}
          </div>
        )}

        <section className="admin-summary-grid">
          <button
            type="button"
            className={`admin-summary-card ${
              activeTab ===
              "pending"
                ? "active"
                : ""
            }`}
            onClick={() => {
              setActiveTab(
                "pending"
              );

              setSelectedCalendarCarId(
                null
              );
            }}
          >
            <span>
              Pending
            </span>

            <strong>
              {pendingCount}
            </strong>

            <small>
              Waiting for
              confirmation
            </small>
          </button>

          <button
            type="button"
            className={`admin-summary-card ${
              activeTab ===
              "confirmed"
                ? "active"
                : ""
            }`}
            onClick={() => {
              setActiveTab(
                "confirmed"
              );

              setSelectedCalendarCarId(
                null
              );
            }}
          >
            <span>
              Confirmed
            </span>

            <strong>
              {confirmedCount}
            </strong>

            <small>
              Active reservations
            </small>
          </button>

          <button
            type="button"
            className={`admin-summary-card ${
              activeTab ===
              "previous"
                ? "active"
                : ""
            }`}
            onClick={() => {
              setActiveTab(
                "previous"
              );

              setSelectedCalendarCarId(
                null
              );
            }}
          >
            <span>
              Previous
            </span>

            <strong>
              {previousCount}
            </strong>

            <small>
              Finished
              reservations
            </small>
          </button>

          <button
            type="button"
            className={`admin-summary-card ${
              activeTab ===
              "all"
                ? "active"
                : ""
            }`}
            onClick={() => {
              setActiveTab(
                "all"
              );

              setSelectedCalendarCarId(
                null
              );
            }}
          >
            <span>
              Total
            </span>

            <strong>
              {bookings.length}
            </strong>

            <small>
              All booking records
            </small>
          </button>
        </section>

        {showAddBooking && (
          <section className="admin-add-booking-card">
            <div className="admin-section-heading">
              <div>
                <span className="admin-eyebrow">
                  MANUAL RESERVATION
                </span>

                <h2>
                  Add Booking
                </h2>
              </div>
            </div>

            <form
              onSubmit={
                handleCreateBooking
              }
            >
              <div className="admin-form-grid">
                <div className="admin-field admin-field-wide">
                  <label>
                    Vehicle *
                  </label>

                  <select
                    name="carId"
                    value={
                      manualBooking.carId
                    }
                    onChange={
                      handleManualChange
                    }
                    required
                  >
                    <option value="">
                      Select a
                      vehicle
                    </option>

                    {cars.map(
                      (car) => (
                        <option
                          key={
                            car.id
                          }
                          value={
                            car.id
                          }
                        >
                          #
                          {
                            car.id
                          }{" "}
                          â€”{" "}
                          {
                            car.name
                          }{" "}
                          â€” â‚¬
                          {
                            car.price
                          }
                          /day
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div className="admin-field">
                  <label>
                    Customer Name *
                  </label>

                  <input
                    type="text"
                    name="customerName"
                    value={
                      manualBooking.customerName
                    }
                    onChange={
                      handleManualChange
                    }
                    required
                  />
                </div>

                <div className="admin-field">
                  <label>
                    Phone *
                  </label>

                  <input
                    type="text"
                    name="customerPhone"
                    value={
                      manualBooking.customerPhone
                    }
                    onChange={
                      handleManualChange
                    }
                    required
                  />
                </div>

                <div className="admin-field">
                  <label>
                    Pickup Date *
                  </label>

                  <input
                    type="date"
                    lang="en-GB"
                    name="pickupDate"
                    value={
                      manualBooking.pickupDate
                    }
                    onChange={
                      handleManualChange
                    }
                    required
                  />
                </div>

                <div className="admin-field">
                  <label>
                    Pickup Time *
                  </label>

                  <select
                    name="pickupTime"
                    value={
                      manualBooking.pickupTime
                    }
                    onChange={
                      handleManualChange
                    }
                  >
                    {timeOptions.map(
                      (time) => (
                        <option
                          key={
                            time
                          }
                          value={
                            time
                          }
                        >
                          {time}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div className="admin-field">
                  <label>
                    Return Date *
                  </label>

                  <input
                    type="date"
                    lang="en-GB"
                    name="returnDate"
                    value={
                      manualBooking.returnDate
                    }
                    onChange={
                      handleManualChange
                    }
                    required
                  />
                </div>

                <div className="admin-field">
                  <label>
                    Return Time *
                  </label>

                  <select
                    name="returnTime"
                    value={
                      manualBooking.returnTime
                    }
                    onChange={
                      handleManualChange
                    }
                  >
                    {timeOptions.map(
                      (time) => (
                        <option
                          key={
                            time
                          }
                          value={
                            time
                          }
                        >
                          {time}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div className="admin-field">
                  <label>
                    Status
                  </label>

                  <select
                    name="status"
                    value={
                      manualBooking.status
                    }
                    onChange={
                      handleManualChange
                    }
                  >
                    <option value="confirmed">
                      Confirmed
                    </option>

                    <option value="pending">
                      Pending
                    </option>
                  </select>
                </div>

                <div className="admin-field admin-field-wide">
                  <label>
                    Private Admin
                    Notes
                  </label>

                  <textarea
                    name="adminNotes"
                    value={
                      manualBooking.adminNotes
                    }
                    onChange={
                      handleManualChange
                    }
                    rows="3"
                  />
                </div>
              </div>

              {manualBooking.carId &&
                manualBooking.pickupDate &&
                manualBooking.returnDate && (
                  <div className="admin-manual-summary">
                    {(() => {
                      const car =
                        getCarById(
                          manualBooking.carId
                        );

                      const days =
                        calculateRentalDays(
                          manualBooking.pickupDate,
                          manualBooking.pickupTime,
                          manualBooking.returnDate,
                          manualBooking.returnTime
                        );

                      const total =
                        car &&
                        days
                          ? Number(
                              car.price
                            ) *
                            days
                          : 0;

                      return (
                        <>
                          <span>
                            Estimated:
                          </span>

                          <strong>
                            {days}{" "}
                            day
                            {days ===
                            1
                              ? ""
                              : "s"}{" "}
                            Â· â‚¬
                            {
                              total
                            }
                          </strong>
                        </>
                      );
                    })()}
                  </div>
                )}

              <div className="admin-form-actions">
                <button
                  type="button"
                  className="admin-secondary-button"
                  onClick={() => {
                    resetManualBooking();

                    setShowAddBooking(
                      false
                    );
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="admin-primary-button"
                  disabled={
                    creatingBooking
                  }
                >
                  {creatingBooking
                    ? "Creating..."
                    : "Create Booking"}
                </button>
              </div>
            </form>
          </section>
        )}

        <AdminFleetCalendar
          cars={cars}
          bookings={bookings}
          selectedCarId={
            selectedCalendarCarId
          }
          onSelectCar={(
            carId
          ) => {
            setSelectedCalendarCarId(
              carId
            );

            if (carId) {
              setActiveTab(
                "all"
              );
            }
          }}
        />

        <section className="admin-bookings-section">
          <div className="admin-bookings-toolbar">
            <div className="admin-tabs">
              <button
                type="button"
                className={
                  activeTab ===
                  "all"
                    ? "active"
                    : ""
                }
                onClick={() => {
                  setActiveTab(
                    "all"
                  );

                  setSelectedCalendarCarId(
                    null
                  );
                }}
              >
                All
              </button>

              <button
                type="button"
                className={
                  activeTab ===
                  "pending"
                    ? "active"
                    : ""
                }
                onClick={() => {
                  setActiveTab(
                    "pending"
                  );

                  setSelectedCalendarCarId(
                    null
                  );
                }}
              >
                Pending
              </button>

              <button
                type="button"
                className={
                  activeTab ===
                  "confirmed"
                    ? "active"
                    : ""
                }
                onClick={() => {
                  setActiveTab(
                    "confirmed"
                  );

                  setSelectedCalendarCarId(
                    null
                  );
                }}
              >
                Confirmed
              </button>

              <button
                type="button"
                className={
                  activeTab ===
                  "previous"
                    ? "active"
                    : ""
                }
                onClick={() => {
                  setActiveTab(
                    "previous"
                  );

                  setSelectedCalendarCarId(
                    null
                  );
                }}
              >
                Previous
              </button>
            </div>

            <button
              type="button"
              className="admin-refresh-button"
              onClick={
                loadBookings
              }
              disabled={
                loading
              }
            >
              {loading
                ? "Loading..."
                : "Refresh"}
            </button>
          </div>

          {loading ? (
            <div className="admin-empty-state">
              Loading bookings...
            </div>
          ) : filteredBookings.length ===
            0 ? (
            <div className="admin-empty-state">
              <div className="admin-empty-icon">
                âœ“
              </div>

              <h3>
                No bookings here
              </h3>

              <p>
                There are
                currently no
                reservations in
                this section.
              </p>
            </div>
          ) : (
            <div className="admin-booking-list">
              {filteredBookings.map(
                (booking) => {
                  const car =
                    getCarById(
                      booking.car_id
                    );

                  const days =
                    calculateRentalDays(
                      booking.pickup_date,
                      booking.pickup_time,
                      booking.return_date,
                      booking.return_time
                    );

                  const total =
                    car &&
                    days
                      ? Number(
                          car.price
                        ) *
                        days
                      : null;

                  const busy =
                    actionLoadingId ===
                    booking.id;

                  return (
                    <article
                      key={
                        booking.id
                      }
                      className="admin-booking-card"
                    >
                      <div className="admin-booking-card-top">
                        <div className="admin-booking-identity">
                          <div className="admin-booking-number">
                            Booking #
                            {
                              booking.id
                            }
                          </div>

                          <div className="admin-booking-created">
                            Created{" "}
                            {booking.created_at
                              ? new Date(
                                  booking.created_at
                                ).toLocaleString(
                                  "en-GB"
                                )
                              : "â€”"}
                          </div>
                        </div>

                        <span
                          className={`admin-status admin-status-${booking.status}`}
                        >
                          {
                            booking.status
                          }
                        </span>
                      </div>

                      <div className="admin-booking-main">
                        <div className="admin-booking-car">
                          <div className="admin-car-image">
                            {car
                              ?.images?.[0] ? (
                              <img
                                src={
                                  car
                                    .images[0]
                                }
                                alt={
                                  car.name
                                }
                              />
                            ) : (
                              <div className="admin-no-car-image">
                                AR
                              </div>
                            )}
                          </div>

                          <div>
                            <span>
                              VEHICLE
                            </span>

                            <h3>
                              {car
                                ? car.name
                                : `Car #${booking.car_id}`}
                            </h3>

                            <p>
                              Car #
                              {
                                booking.car_id
                              }
                              {car
                                ? ` Â· â‚¬${car.price}/day`
                                : ""}
                            </p>
                          </div>
                        </div>

                        <div className="admin-booking-info-grid">
                          <div>
                            <span>
                              CUSTOMER
                            </span>

                            <strong>
                              {
                                booking.customer_name
                              }
                            </strong>

                            <a
                              href={`tel:${booking.customer_phone}`}
                            >
                              {
                                booking.customer_phone
                              }
                            </a>
                          </div>

                          <div>
                            <span>
                              PICKUP
                            </span>

                            <strong>
                              {formatDate(
                                booking.pickup_date
                              )}
                            </strong>

                            <p>
                              {formatTime(
                                booking.pickup_time
                              )}
                            </p>
                          </div>

                          <div>
                            <span>
                              RETURN
                            </span>

                            <strong>
                              {formatDate(
                                booking.return_date
                              )}
                            </strong>

                            <p>
                              {formatTime(
                                booking.return_time
                              )}
                            </p>
                          </div>

                          <div>
                            <span>
                              PRICE
                            </span>

                            <strong>
                              {days}{" "}
                              day
                              {days ===
                              1
                                ? ""
                                : "s"}
                            </strong>

                            <p>
                              {total !==
                              null
                                ? `â‚¬${total}`
                                : "â€”"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="admin-notes-section">
                        <label>
                          Private Admin
                          Notes
                        </label>

                        <textarea
                          value={
                            notes[
                              booking.id
                            ] ||
                            ""
                          }
                          onChange={(
                            event
                          ) =>
                            setNotes(
                              (
                                current
                              ) => ({
                                ...current,

                                [booking.id]:
                                  event
                                    .target
                                    .value,
                              })
                            )
                          }
                          placeholder="Add private notes about this reservation..."
                          rows="3"
                        />

                        <button
                          type="button"
                          className="admin-save-notes-button"
                          onClick={() =>
                            handleSaveNotes(
                              booking
                            )
                          }
                          disabled={
                            busy
                          }
                        >
                          Save Notes
                        </button>
                      </div>

                      <div className="admin-booking-actions">
                        {booking.status ===
                          "pending" && (
                          <>
                            <button
                              type="button"
                              className="admin-confirm-button"
                              onClick={() =>
                                handleConfirm(
                                  booking
                                )
                              }
                              disabled={
                                busy
                              }
                            >
                              Confirm
                            </button>

                            <button
                              type="button"
                              className="admin-reject-button"
                              onClick={() =>
                                handleReject(
                                  booking
                                )
                              }
                              disabled={
                                busy
                              }
                            >
                              Reject
                            </button>
                          </>
                        )}

                        {booking.status ===
                          "confirmed" && (
                          <button
                            type="button"
                            className="admin-cancel-button"
                            onClick={() =>
                              handleCancel(
                                booking
                              )
                            }
                            disabled={
                              busy
                            }
                          >
                            Cancel
                            Booking
                          </button>
                        )}

                        <button
                          type="button"
                          className="admin-delete-button"
                          onClick={() =>
                            handleDelete(
                              booking
                            )
                          }
                          disabled={
                            busy
                          }
                        >
                          Delete
                          Permanently
                        </button>
                      </div>
                    </article>
                  );
                }
              )}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default AdminBookings;